/**
 * The offline queue, in IndexedDB.
 *
 * ## What is queued and why
 *
 * Two things are worth finishing after the network comes back: a photographed
 * receipt and a logged trip. Both are captured in the one place this
 * application is used without a connection — standing beside a car — and both
 * are lost for good if the request that carries them fails. Everything else the
 * app does is done at a desk, where a failed request can simply be retried by
 * the person who made it.
 *
 * ## Why IndexedDB and not the Cache API
 *
 * Workbox's `BackgroundSyncPlugin` stores the *whole request* and replays it
 * verbatim. That cannot work here: a replayed request carries the company
 * access token it was created with, and those last fifteen minutes (ADR-007).
 * A photo taken on Friday evening and replayed on Monday morning would be
 * rejected with 401 and dropped from the queue.
 *
 * So what is stored is the *intent* — "upload this file", "log this trip" — and
 * the sender mints a fresh token at the moment it sends. That is also why this
 * module is deliberately free of anything but IndexedDB: it is imported by the
 * page and by the service worker, which have different globals, and the only
 * thing both have is this.
 *
 * ## Why this file is shared rather than written twice
 *
 * A second copy in the worker would drift, and the failure mode of drift here
 * is a queue one side can write and the other cannot read — which presents as
 * receipts that vanish.
 */

const DATABASE = 'accountyob-offline';
const VERSION = 1;

/** Queued work, oldest first. */
export const QUEUE_STORE = 'queue';
/**
 * What the sender needs in order to mint a token: where the API is, and which
 * company the work belongs to.
 *
 * Never a token itself. A company access token lives fifteen minutes and would
 * be stale before most of this queue is drained; a Better Auth session cookie
 * is httpOnly and is sent by the browser, not read by this code.
 */
export const CONTEXT_STORE = 'context';

/** The tag the page registers and the worker listens for. */
export const SYNC_TAG = 'accountyob-drain-queue';

export type QueuedKind = 'attachment' | 'trip';

export interface QueuedAttachment {
  readonly kind: 'attachment';
  readonly filename: string;
  readonly mimeType: string;
  /** The bytes, base64. A Blob would be smaller, but Safari has lost Blobs
   * stored in IndexedDB across restarts, and this queue exists precisely for
   * the case where the app is closed and reopened later. */
  readonly contentBase64: string;
}

export interface QueuedTrip {
  readonly kind: 'trip';
  readonly date: string;
  readonly startAddress: string;
  readonly endAddress: string;
  readonly distanceKm: string;
  readonly purpose: string;
  readonly isBusiness: boolean;
}

export type QueuedPayload = QueuedAttachment | QueuedTrip;

export interface QueuedItem {
  readonly id: number;
  readonly companyId: string;
  readonly createdAt: number;
  /**
   * How many times sending has been attempted.
   *
   * Kept so a single item that the server will never accept — a photo of
   * something that is not an allowed type, say — cannot block the queue behind
   * it for ever. See MAX_ATTEMPTS in sync.ts.
   */
  readonly attempts: number;
  /** The last thing the server or the network said, for the page to show. */
  readonly lastError?: string | undefined;
  readonly payload: QueuedPayload;
  /**
   * The client-generated idempotency key.
   *
   * Generated when the item is queued, not when it is sent, and reused across
   * every attempt. That is what makes a retry safe after a response was lost:
   * the API recognises the key and returns the original result rather than
   * filing the receipt twice.
   */
  readonly idempotencyKey: string;
}

export interface QueueContext {
  readonly id: 'current';
  readonly apiBaseUrl: string;
  readonly companyId: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        const store = db.createObjectStore(QUEUE_STORE, {
          keyPath: 'id',
          autoIncrement: true,
        });
        // Drained oldest first, so a receipt photographed before a trip was
        // logged is filed before it.
        store.createIndex('createdAt', 'createdAt');
      }
      if (!db.objectStoreNames.contains(CONTEXT_STORE)) {
        db.createObjectStore(CONTEXT_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('Could not open the offline queue.'));
    };
  });
}

function run<T>(
  storeName: string,
  mode: IDBTransactionMode,
  body: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const request = body(transaction.objectStore(storeName));
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = () => {
          reject(request.error ?? new Error('The offline queue rejected the operation.'));
        };
        transaction.oncomplete = () => {
          db.close();
        };
      }),
  );
}

export async function enqueue(
  item: Omit<QueuedItem, 'id' | 'attempts' | 'createdAt'>,
): Promise<number> {
  const key = await run<IDBValidKey>(QUEUE_STORE, 'readwrite', (store) =>
    store.add({ ...item, createdAt: Date.now(), attempts: 0 }),
  );
  return Number(key);
}

/** Everything waiting, oldest first. */
export async function listQueue(companyId?: string): Promise<QueuedItem[]> {
  const all = await run<QueuedItem[]>(QUEUE_STORE, 'readonly', (store) => store.getAll());
  const ordered = [...all].sort((left, right) => left.createdAt - right.createdAt);
  return companyId === undefined
    ? ordered
    : ordered.filter((item) => item.companyId === companyId);
}

export async function removeFromQueue(id: number): Promise<void> {
  await run(QUEUE_STORE, 'readwrite', (store) => store.delete(id));
}

/** Record a failed attempt without losing the item. */
export async function recordAttempt(id: number, error: string): Promise<void> {
  const existing = await run<QueuedItem | undefined>(QUEUE_STORE, 'readonly', (store) =>
    store.get(id),
  );
  if (existing === undefined) {
    return;
  }
  await run(QUEUE_STORE, 'readwrite', (store) =>
    store.put({ ...existing, attempts: existing.attempts + 1, lastError: error }),
  );
}

/**
 * Give a stuck item its attempts back, so the next drain tries it again.
 *
 * For the case a person has fixed whatever the server objected to -- signed in
 * again, been given the role back -- and wants the receipt sent rather than
 * discarded.
 */
export async function resetAttempts(id: number): Promise<void> {
  const existing = await run<QueuedItem | undefined>(QUEUE_STORE, 'readonly', (store) =>
    store.get(id),
  );
  if (existing === undefined) {
    return;
  }
  const { lastError: _discarded, ...rest } = existing;
  await run(QUEUE_STORE, 'readwrite', (store) => store.put({ ...rest, attempts: 0 }));
}

export async function saveContext(context: Omit<QueueContext, 'id'>): Promise<void> {
  await run(CONTEXT_STORE, 'readwrite', (store) => store.put({ id: 'current', ...context }));
}

export async function loadContext(): Promise<QueueContext | undefined> {
  return run<QueueContext | undefined>(CONTEXT_STORE, 'readonly', (store) =>
    store.get('current'),
  );
}
